import { useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import reportService from '@api/v1/reportService'
import reportJobService from '@api/v1/reportJobService'
import reportScheduleService from '@api/v1/reportScheduleService'
import { useInvalidateSchedules } from '@hooks/useSchedules'
import useReadState from '@hooks/useReadState'
import { filtersAsArray } from '@utils/reportFilterWire'
import { buildInitialFilters } from '@utils/reportFilterDefaults'
import { unsupportedQueuePick } from '@utils/queueableReport'
import { toast } from '@store/toastStore'

/**
 * The "pick a report, then queue or schedule it" flow, extracted so there is ONE
 * of it (2026-08-27).
 *
 * ## Why this exists
 *
 * `QueueReportModal` is rendered from four hosts. Two of them already have a
 * report in hand — `ReportView` and `SpecializedFilterBar` pass the report and
 * its on-screen filter values straight in. The other two do NOT: they open the
 * modal with no report chosen, so they need a picker, a definition read, and the
 * filter seeding that turns "report #190" into a scoped run. That second flow
 * lived only in `ReportQueue.jsx`, which is why the schedules page's
 * "New schedule" button was a `<Link>` to the report catalog instead
 * (`ReportSchedules.jsx:245`, introduced by 02604d9 relabelling a
 * "Browse reports" link without changing where it went).
 *
 * Building it a second time on the schedules page was the option NOT taken:
 * this repo's expensive pathology is one capability implemented N times
 * (CC-50282's scope selector built three times; ObjectLookup/LookupFilter). So
 * the machinery moved here and both hosts call it.
 *
 * ## Why it returns `modalProps` rather than fifteen loose values
 *
 * The whole point of the extraction is that both hosts render an IDENTICALLY
 * wired modal. Handing back fifteen pieces and asking each host to re-spell them
 * re-opens the drift this hook closes — and the drift is not cosmetic: the
 * `columnsError` and `unsupported` props are the two guards that stop a run
 * being booked with filters we could not read. A host that forgets one of them
 * books an UNSCOPED run of a financial report, which CB reports as a success.
 * So the hook owns the wiring and the host owns only where the modal appears.
 *
 * The single per-host decision is `initialWhen`, which is why it is an explicit
 * option with no silent default at the call sites — see below.
 *
 * ## Money logic
 *
 * `buildInitialFilters` → `filtersAsArray` is the path that gives a queued or
 * scheduled run its scope. A schedule created with an empty `filters` array runs
 * CB's stored proc unscoped and emails the result to a firm; CB's Newtonsoft
 * deserialization ignores unknown/missing filter keys silently, so nothing
 * anywhere reports the mistake. Both guards below refuse to submit rather than
 * submit something unscoped.
 *
 * @param {object} [opts]
 * @param {'now'|'once'|'repeat'} [opts.initialWhen]
 *   Which "when it runs" choice the modal opens on. **Pass it explicitly.**
 *   `QueueReportModal` defaults to `'now'` (QueueReportModal.jsx:67), so a host
 *   whose button promises a schedule and omits this opens on "Right now, in the
 *   background" — the same wrong-promise defect as the old catalog link, just
 *   quieter. `ReportView.jsx` and `SpecializedFilterBar.jsx` both steer it for
 *   the same reason.
 * @returns {{isOpen: boolean, open: () => void, close: () => void, modalProps: object}}
 */
export function useQueueReportPicker({ initialWhen = 'now' } = {}) {
  const [modalOpen, setModalOpen] = useState(false)
  const [pickedReportId, setPickedReportId] = useState('')
  const [modalFilters, setModalFilters] = useState({})
  const [queueing, setQueueing] = useState(false)
  const openModal = useCallback(() => setModalOpen(true), [])
  const closeModal = useCallback(() => setModalOpen(false), [])

  const catalogQuery = useQuery({
    queryKey: ['reports-list'],
    queryFn: () => reportService.list(''),
    staleTime: 5 * 60 * 1000,
    enabled: modalOpen,
  })
  const { data: catalogTabs } = catalogQuery

  /*
   * Three states, not two — see `@api/v1/readState` (CC-53132).
   *
   * This query destructured `data` and nothing else, so a read that never happened
   * produced a report picker holding one option: "Choose a report…". No spinner, no
   * error, no request — react-query PAUSES a query while it believes the browser is
   * offline, `queryFn` is never called and `data` stays undefined. On the flow whose
   * entire purpose is queueing a report that is a dead end the user cannot read or
   * retry, and it is indistinguishable from a firm with no reports at all.
   *
   * `idle` needs no surface: the only thing that switches this query off is the modal
   * being closed, and then there is nothing on screen to be wrong.
   */
  const catalogState = useReadState('queue-report-catalog', catalogQuery)
  const catalogUnreadable = catalogState === 'blocked' || catalogState === 'failed'

  const invalidateSchedules = useInvalidateSchedules()

  // `actionKey` rides along: it is the ONLY thing that distinguishes a report
  // this picker can queue from one it cannot. The catalog payload already carries
  // it (reports/routes.js `/list`), so no cb-api change is needed.
  const reportOptions = useMemo(() => {
    const seen = new Map()
    for (const t of catalogTabs || []) {
      for (const r of t.reports || []) {
        if (r?.id != null && !seen.has(r.name)) seen.set(r.name, { id: r.id, name: r.name, actionKey: r.actionKey || '' })
      }
    }
    return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name))
  }, [catalogTabs])

  const pickedOption = useMemo(
    () => reportOptions.find((r) => String(r.id) === String(pickedReportId)) || null,
    [reportOptions, pickedReportId]
  )

  // Specialized reports can't be queued from this picker and their filters
  // can't be shown here — same root cause, both explained in queueableReport.js.
  const unsupportedPick = useMemo(() => unsupportedQueuePick(pickedOption), [pickedOption])

  const definitionQuery = useQuery({
    queryKey: ['report-definition', pickedReportId],
    queryFn: () => reportService.getDefinition(pickedReportId),
    // Don't spend a definition read on a report we already know we can't queue.
    enabled: modalOpen && Boolean(pickedReportId) && !unsupportedPick,
  })
  const {
    data: pickedDefinition,
    isFetching: loadingColumns,
    refetch: refetchDefinition,
  } = definitionQuery

  /*
   * The severe one on this flow (CC-53132). The modal will not say "This report has
   * no filters." unless the caller tells it the read finished — but `isError` cannot
   * tell it that. `isError` is FALSE for a paused read: nothing threw, because nothing
   * was ever sent. So a blocked definition read reached the modal as "read finished,
   * no filters", and the run booked under it would go to CB UNSCOPED — every matter,
   * every date. On a WIP or A/R report that is a wrong number with a plausible face.
   *
   * `blocked` and `failed` are therefore both `columnsError`; the modal already
   * disables submit on it, so both halves ride on this one predicate.
   *
   * `idle` is NOT included, and must not be: this query is disabled until a report is
   * picked, so idle is the ordinary "nothing chosen yet" state and surfacing it would
   * put an error on a modal the user has only just opened.
   */
  const definitionState = useReadState('queue-report-definition', definitionQuery)
  const definitionUnreadable = definitionState === 'blocked' || definitionState === 'failed'

  // Seed each filter with the report's own default through the SAME helper the
  // report page uses — otherwise a queued run silently uses empty/unresolved
  // filters, and an empty DATE makes CB's proc throw.
  useEffect(() => {
    if (!pickedDefinition) return
    setModalFilters(buildInitialFilters(pickedDefinition))
  }, [pickedDefinition])

  const pickedName = useCallback(() => pickedOption?.name || 'Report', [pickedOption])

  // One submit path for all three "when" choices — they differ only in what they
  // send, so branching here keeps the modal a pure form.
  const submitQueue = useCallback(async (mode, payload, delivery = {}) => {
    if (!pickedReportId) return
    // Belt and braces: the modal already disables submit for these, but a
    // generic job for a specialized report is a run that reports success and
    // then fails, so refuse it here too rather than trust one guard.
    if (unsupportedPick) return
    // Same belt-and-braces as above, for the reason in `definitionUnreadable`: the
    // modal disables submit on `columnsError`, but a run booked without the report's
    // own filters goes to CB unscoped, so refuse it here too rather than trust one
    // guard. This is a financial application — a visible refusal beats a plausible
    // wrong number.
    if (definitionUnreadable) return
    setQueueing(true)
    try {
      const filters = filtersAsArray(modalFilters, pickedDefinition?.columns, pickedDefinition?.reportType)
      const name = pickedName()
      if (mode === 'schedule') {
        await reportScheduleService.create({
          kind: 'generic',
          reportId: pickedReportId,
          name: payload.name || name,
          filters,
          recurrence: payload.recurrence,
          rollForward: payload.rollForward,
          retainCount: payload.retainCount,
          // Delivery prefs come in on the schedule payload (modal spreads them).
          deliverEmail: payload.deliverEmail,
          deliverFormat: payload.deliverFormat,
          // Spec 138: the schedule's other recipients. cb-api validates every entry
          // and is the authority; this host only forwards what the modal collected.
          deliverRecipients: payload.deliverRecipients,
          // Spec 139: the address an owner with no email of their own supplied — present
          // only when the modal asked for it, and absent (no key) otherwise. cb-api
          // decides whether it is accepted.
          ...(payload.deliverOwnerAddress !== undefined ? { deliverOwnerAddress: payload.deliverOwnerAddress } : {}),
          // Visibility (CC-50282). The route has accepted these since the scope
          // model shipped; only this create path never sent them, so a schedule
          // made here was always private no matter what the modal showed. The
          // server validates `scope`/`groupId` against CB group membership and
          // defaults an absent scope to 'mine'.
          scope: payload.scope,
          groupId: payload.groupId,
          groupName: payload.groupName,
        })
        // Without this the new schedule was invisible until a hard refresh: the
        // Queue page's count chip and the Manage Schedules list both read the same
        // cached key, and nothing marked it stale. `SCHEDULES_QUERY_KEY` is the
        // key PREFIX, so this clears every scope — including the schedules page's
        // own `['report-schedules', 'mine'|'group'|'firm']` entry, which is what
        // makes a schedule created there appear in the list underneath it.
        invalidateSchedules()
        toast.success(`${name} is scheduled`)
      } else {
        await reportJobService.create({
          kind: 'generic',
          reportId: pickedReportId,
          filters,
          ...(mode === 'later' ? { scheduledFor: payload } : {}),
          // Forward the email/delivery choice — without this the Queue page dropped
          // it, so "Email me when it's ready" never reached the backend.
          ...(delivery.deliverEmail ? { deliverEmail: true, deliverFormat: delivery.deliverFormat } : {}),
        })
        toast.success(mode === 'later' ? `${name} is scheduled to run` : `${name} is in your queue`)
      }
      setModalOpen(false)
      setPickedReportId('')
    } catch (err) {
      toast.error(err?.message || 'Could not queue that report.')
    } finally {
      setQueueing(false)
    }
  }, [pickedReportId, modalFilters, pickedDefinition, pickedName, invalidateSchedules, unsupportedPick, definitionUnreadable])

  const queueNow = useCallback((delivery) => submitQueue('now', null, delivery), [submitQueue])
  const queueLater = useCallback((at, delivery) => submitQueue('later', at, delivery), [submitQueue])
  const queueSchedule = useCallback((cfg) => submitQueue('schedule', cfg), [submitQueue])

  const selectReport = useCallback((id) => { setPickedReportId(id); setModalFilters({}) }, [])
  const changeFilter = useCallback((colId, v) => setModalFilters((prev) => ({ ...prev, [colId]: v })), [])

  const modalProps = {
    reportOptions,
    selectedReportId: pickedReportId,
    onSelectReport: selectReport,
    report: pickedOption ? { id: pickedOption.id, name: pickedDefinition?.name || pickedOption.name } : null,
    columns: (pickedDefinition?.columns || []).filter((c) => c.filterable !== false && c.filterTypeName && c.filterTypeName !== 'HIDDEN'),
    values: modalFilters,
    onChange: changeFilter,
    accountIds: pickedDefinition?.filteredAccountIds,
    loadingColumns,
    // A definition read that FAILED — or that was never sent at all — must NOT
    // read as "this report has no filters". That is the empty-vs-broken trap,
    // and the run booked under it would be unscoped.
    columnsError: definitionUnreadable,
    onRetryColumns: refetchDefinition,
    unsupported: unsupportedPick,
    isSubmitting: queueing,
    initialWhen,
    onQueueNow: queueNow,
    onQueueLater: queueLater,
    onSchedule: queueSchedule,
    onClose: closeModal,
  }

  return {
    isOpen: modalOpen,
    open: openModal,
    close: closeModal,
    modalProps,
    /*
     * CC-53132 — the catalog read, surfaced to the HOST rather than handled here.
     *
     * `QueueReportModal` renders the picker before any error branch it has, so there
     * is no slot inside it that can carry this; and it would be the wrong noun anyway
     * (the report LIST failed, not one report's filters). Both hosts render their own
     * shell for it, so focus trap, Escape and labelling stay intact.
     */
    catalogState,
    catalogUnreadable,
    catalogError: catalogQuery.error,
    retryCatalog: catalogQuery.refetch,
  }
}

export default useQueueReportPicker
